import { Link } from "react-router-dom";
import { MapPin, Calendar, ShieldCheck, Wrench } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { ListingTypeBadge } from "@/components/ListingTypeBadge";
import { fileUrl, formatPrice, listingBasePath } from "@/lib/api";

export function MachineCard({ m, onEnquire }) {
  const img = m.images?.[0]?.url;
  const isSold = m.status === "sold";
  const isNew = m.listing_type === "new";
  const isSpare = m.listing_type === "spare";
  const detailPath = `${listingBasePath(m.listing_type)}/${m.slug}`;

  return (
    <div
      data-testid={`machine-card-${m.machine_code}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-lg"
    >
      <Link to={detailPath} className="relative block aspect-[4/3] overflow-hidden bg-slate-100">
        {img ? (
          <img
            src={fileUrl(img)}
            alt={m.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300">No Image</div>
        )}
        <div className="absolute left-3 top-3 flex flex-col gap-1.5">
          <ListingTypeBadge type={m.listing_type} />
          {isSold && <StatusBadge status="sold" />}
        </div>
        {isNew && !isSold && (
          <div className="absolute right-3 top-3">
            <span data-testid={`warranty-badge-${m.machine_code}`} className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-100 px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
              <ShieldCheck size={12} /> Warranty
            </span>
          </div>
        )}
        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/45">
            <span className="rotate-[-8deg] rounded-md border-4 border-red-500 px-5 py-1 font-display text-3xl font-extrabold uppercase tracking-widest text-red-500">
              Sold
            </span>
          </div>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <span className="font-mono text-[11px] font-semibold text-slate-400">{m.machine_code}</span>
        <Link to={detailPath}>
          <h3 className="mt-0.5 line-clamp-2 font-display text-base font-bold leading-snug text-slate-900 hover:text-[hsl(var(--brand))]">
            {m.name}
          </h3>
        </Link>
        <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-500">
          {m.brand && <span className="font-semibold text-slate-700">{m.brand}</span>}
          {m.model && <span>{m.model}</span>}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          {m.year && <span className="inline-flex items-center gap-1"><Calendar size={12} />{m.year}</span>}
          {m.condition && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{m.condition}</span>}
        </div>
        {isSpare && (m.compatible_brand || m.part_number) && (
          <div className="mt-2 space-y-0.5 text-xs text-slate-500">
            {m.compatible_brand && <div className="inline-flex items-center gap-1"><Wrench size={12} className="text-[hsl(var(--charcoal))]" /> Fits: {m.compatible_brand}{m.compatible_machine ? ` ${m.compatible_machine}` : ""}</div>}
            {m.part_number && <div className="font-mono text-[11px] text-slate-400">Part #: {m.part_number}</div>}
          </div>
        )}
        {(m.city || m.state) && (
          <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
            <MapPin size={12} className="brand-accent" />
            {[m.city, m.state].filter(Boolean).join(", ")}
          </div>
        )}

        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="font-display text-lg font-extrabold text-slate-900">{formatPrice(m.price, m.price_type)}</p>
          {m.price_type === "Negotiable" && m.price > 0 && (
            <span className="text-[11px] font-medium text-slate-400">Negotiable</span>
          )}
        </div>

        <div className="mt-3 flex gap-2">
          <Link
            to={detailPath}
            data-testid={`view-details-${m.machine_code}`}
            className="flex-1 rounded-lg border border-slate-300 py-2 text-center text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50"
          >
            VIEW DETAILS
          </Link>
          {isSold ? (
            <span className="flex-1 rounded-lg bg-slate-100 py-2 text-center text-xs font-bold text-slate-400">SOLD</span>
          ) : (
            <button
              data-testid={`enquire-now-${m.machine_code}`}
              onClick={() => onEnquire?.(m)}
              className="flex-1 rounded-lg bg-brand py-2 text-center text-xs font-bold text-white transition-colors"
            >
              ENQUIRE NOW
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
